import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { PetSpeciesFilterableType } from 'src/app/interfaces/pet';
import { ProductType, User } from 'src/app/interfaces/user';
import { PlanFeatureService } from 'src/app/services/common/plan-feature.service';
import { MemberShipOptionsService } from 'src/app/services/membership-options/membership-options.service';
import { UserService } from 'src/app/services/user/user.service';
import { ConfigService } from '../../../services/config/config.service';
import { FeatureFlagsService } from 'src/app/services/feature-flag/feature-flag.service';
import { Router } from '@angular/router';
import { PaymentConfigService } from 'src/app/services/payment-config/payment-config.service';
import { PricingInfoLine } from 'src/app/shared/components/pricing-table/price-card/price-card.component';

export enum PricingTableSubTypes {
  IDENTIBASEPLUS = 'IDENTIBASEPLUS',
  IDENTIALERT = 'IDENTIALERT',
  SMARTTAG = 'SMARTTAG',
}

@Component({
  selector: 'app-pricing-table',
  templateUrl: './pricing-table.component.html',
  styleUrls: ['./pricing-table.component.scss'],
})
export class PricingTableComponent implements OnInit, OnChanges {
  @Input() disabled: boolean;
  @Input() pricingPlans: any;
  @Input() viewOnly: boolean;
  @Input() public user: User;
  @Input() buttonLabel: string;
  @Input() hidePrices: boolean;
  @Input() showShortDescription: boolean;
  @Input() showInfoLine: boolean;
  @Input() showFeatures: boolean;
  @Input() showPromo: boolean;
  @Input() petName: string;
  @Input() showWideIdentiAlertCard: boolean;
  @Input() hideIcons: boolean;
  @Input() hasBuyNow: boolean;
  @Input() appPriceCardFeatureText: string[];
  @Input() appAlertPriceCardFeatureText: string[];
  @Input() showPeachBanner: boolean;
  @Input() userCurrentProductType: number;
  @Input() isDashboard: boolean = false;
  @Input() showShortDescriptionList: boolean;
  /**
   * When provided, overrides the user-based card availability rules with the list from the
   * registration payment config.
   */
  @Input() availableProductIds: number[] | null = null;

  @Output() onClosePromo = new EventEmitter<string>();

  @Output() onSelect: EventEmitter<string> = new EventEmitter();
  @Output() onNavigateToProductPage: EventEmitter<string> = new EventEmitter();

  public doesTheUserHaveCat: boolean = false;
  public doesTheUserHaveDog: boolean = false;
  public petType: PetSpeciesFilterableType;
  public thereIsOnlyOneCat: boolean = false;
  public isOutdoors: boolean = null;
  public productType = ProductType;
  public planIdIdentiAlert: string;
  public PricingTableSubTypes = PricingTableSubTypes;
  public promoPopupOpen: boolean = false;
  public doesTheUserHaveOnlyCat: boolean = false;
  public smartTagSubAvailable: boolean = true;
  public smartTagProductId: string;
  public identibasePlusAvailable: boolean = true;
  public identiAlertAvailable: boolean = true;
  public showTitles: boolean = false;
  public mainPlanContent = [];
  public isBrowser: boolean;
  public plan2DailyPrice: string;
  public brandName: string;
  public plan1DailyPrice: string;
  public plan1BenefitsShort: string[];
  public plan2BenefitsShort: string[];
  public plan3BenefitsShort: string[];
  public showPremiumCardColoredBorder: boolean;

  public plan1Name: string;
  public plan1YearlyPrice: string;
  public plan2Name: string;
  public plan2YearlyPrice: string;
  public plan2MonthlyPrice: string;
  public plan1BenefitsCat: string[];
  public plan1Benefits: string[];
  public plan2Benefits: string[];

  public plan3Name: string;
  public plan3YearlyPrice: string;

  public smartTagPrice: string;

  constructor(
    public userService: UserService,
    public membershipOptionsService: MemberShipOptionsService,
    public planFeatureService: PlanFeatureService,
    private configService: ConfigService,
    private router: Router,
    private featureFlagService: FeatureFlagsService,
    private paymentConfigService: PaymentConfigService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.brandName = this.configService.getTextFromKey('brand-name');

    this.plan1DailyPrice = this.configService.getTextFromKey('1-plan-daily-price');
    this.plan1YearlyPrice = this.configService.getTextFromKey('1-plan-yearly-price');
    this.plan1Name = this.configService.getTextFromKey('1-plan-name');
    this.plan1BenefitsCat = this.configService.getListContentFromKey('1-plan-benefits-cat');
    this.plan1Benefits = this.configService.getListContentFromKey('1-plan-benefits');

    this.plan2DailyPrice = this.configService.getTextFromKey('2-plan-daily-price');
    this.plan2Name = this.configService.getTextFromKey('2-plan-name');
    this.plan2YearlyPrice = this.configService.getTextFromKey('2-plan-yearly-price');
    this.plan2MonthlyPrice = this.configService.getTextFromKey('2-plan-monthly-price');
    this.plan2Benefits = this.configService.getListContentFromKey('2-plan-benefits');

    this.plan3Name = this.configService.getTextFromKey('3-plan-name');
    this.plan3YearlyPrice = this.configService.getTextFromKey('3-plan-yearly-price');
    this.smartTagPrice = this.configService.getTextFromKey('3-plan-tag-price');

    this.plan1BenefitsShort = this.configService.getListContentFromKey('1-plan-benefits-short');
    this.plan2BenefitsShort = this.configService.getListContentFromKey('2-plan-benefits-short');
    this.plan3BenefitsShort = this.configService.getListContentFromKey('3-plan-benefits-short');
    this.showPremiumCardColoredBorder = this.featureFlagService.isEnabled('showPremiumCardColoredBorder');

    // Backend /payment amounts override the displayed prices; daily prices have no /payment equivalent.
    this.plan1YearlyPrice =
      this.paymentConfigService.getYearlyDisplayPrice(ProductType.BasicPlus) ?? this.plan1YearlyPrice;
    this.plan2YearlyPrice =
      this.paymentConfigService.getYearlyDisplayPrice(ProductType.Premium) ?? this.plan2YearlyPrice;
    this.plan2MonthlyPrice = this.paymentConfigService.getMonthlyDisplayPrice(
      ProductType.Premium,
      this.plan2MonthlyPrice
    );
    this.plan3YearlyPrice =
      this.paymentConfigService.getYearlyDisplayPrice(ProductType.Locator) ?? this.plan3YearlyPrice;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.showPromo) {
      this.showPromo = changes.showPromo.currentValue;
      this.promoPopupOpen = this.showPromo;
    }
  }

  closePromoPopup() {
    this.promoPopupOpen = false;
    this.onClosePromo.emit();
  }

  getFeaturesFor(plan: string): any[] {
    return this.planFeatureService.getFeaturesFor(plan);
  }

  getMainPlanCardContent(petName?: string) {
    return this.planFeatureService.getMainPlanContent(petName);
  }

  select(planId?: string): void {
    if (planId === "smartTag") {
      this.router.navigate(['/get-identi-smart-tag']);
      return;
    }
    this.onSelect.emit(planId);
  }

  trackEvent(event: string) {
    if (!this.isBrowser) return;
    window.analytics.track(event);
  }

  ngOnInit(): void {
    if (!this.viewOnly) {
      this.planIdIdentiAlert = this.pricingPlans.identiAlertAnnual;
    }

    if (this.user) {
      this.doesTheUserHaveCat = !!this.user.pets.find(
        (pet) => pet.speciesFilter === PetSpeciesFilterableType.Cat
      );
      this.doesTheUserHaveDog = !!this.user.pets.find(
        (pet) => pet.speciesFilter === PetSpeciesFilterableType.Dog
      );

      if (this.doesTheUserHaveCat && !this.doesTheUserHaveDog) {
        this.doesTheUserHaveOnlyCat = true;
      }

      // Mixed households leave petType unset, matching the old base flow's fallback image/copy.
      if (this.doesTheUserHaveDog && !this.doesTheUserHaveCat) {
        this.petType = PetSpeciesFilterableType.Dog;
      } else if (this.doesTheUserHaveCat && !this.doesTheUserHaveDog) {
        this.petType = PetSpeciesFilterableType.Cat;
      }

      this.thereIsOnlyOneCat =
        this.user.pets.length === 1 && this.user.pets[0].speciesFilter === PetSpeciesFilterableType.Cat;
      this.isOutdoors = this.user.pets[0]?.isOutdoors ?? null;

      if (
        this.userService.isUserAlertOrFind(this.user.productType) ||
        this.user.productType === ProductType.BasicPlus
      ) {
        this.smartTagProductId = this.pricingPlans.smartTagForAlertAndFind;
      } else {
        this.smartTagProductId = this.pricingPlans.smartTagForBase;
      }

      this.identibasePlusAvailable = this.membershipOptionsService.identiBasePlusAvailable(
        this.user
      );
      this.identiAlertAvailable = this.membershipOptionsService.identiAlertAvailable(this.user);
    }

    this.applyAvailableProductIds();
    this.mainPlanContent = this.getMainPlanCardContent(this.petName);
  }

  /**
   * When availableProductIds is provided, it overrides the user-based card availability set above.
   * It does nothing when the input is absent, so the existing feature-flag and user-based behaviour
   * stays in place.
   */
  private applyAvailableProductIds(): void {
    const availability = this.paymentConfigService.getProductAvailability(this.availableProductIds);
    if (!availability) return;
    this.identibasePlusAvailable = availability.basicPlus;
    this.identiAlertAvailable = availability.premium;
    this.smartTagSubAvailable = availability.smartTag;
  }

  public doesTheUserHaveOnlyCats() {
    return this.doesTheUserHaveCat && !this.doesTheUserHaveDog;
  }
}
